<template>
  <NrModal
    class="document-upload-modal"
    :show="show"
    hasSecondaryButton
    primaryButtonText="Save"
    :disableButtons="!readyToSave"
    title="Add documents"
    @on-primary-click="submitUploadForm"
    @on-secondary-click="onClose"
  >
    <form @submit.prevent>
      <NrTabs v-model="activeTabId">
        <NrTab title="Upload" id="documents-upload-tab">
          <DragNDrop
            :upload-limit="uploadLimit"
            :account="account"
            :request-id="requestId"
            :s3-bucket="s3UploadConfig.bucket"
            :evaporate="evaporate"
            @on-all-file-promises-resolved="allFilesUploadedToS3 = true"
            @on-file-drop="allFilesUploadedToS3 = false"
            @on-file-upload-complete="$emit('on-file-upload-complete', $event)"
          />
        </NrTab>
        <NrTab title="Links" id="documents-links-tab">
          <NrFormInput
            v-model="docLink"
            label="Document link"
            placeholder="https://www.yourcity.gov/documents/notes.pdf"
            aria-label="Enter document link"
            @input="setDocumentLink(docLink)"
          />
          <span v-if="invalidUrl" class="has-text-danger">
            Please enter a valid URL starting with https://
          </span>
          <NrDatePicker v-model="docLinkDate" placeholder="Date (mm/dd/yyyy)">
            <template v-slot:label>Document Date</template>
            <template v-slot:trigger>
              <NrIcon name="calendar-alt" />
            </template>
          </NrDatePicker>
          <NrFormInput
            v-model="docLinkDescription"
            label="Description (optional)"
            placeholder="Description"
            aria-label="Enter document description"
            maxLength="300"
            tag="textarea"
            @input="setDocLinkDescription(docLinkDescription)"
          />
        </NrTab>
      </NrTabs>

      <NrSelect
        v-if="enableFolderAssignment"
        :value="selectedFolder"
        taggable
        :options="folders"
        placeholder="Search and select a folder"
        @input="handleSelectedFolder"
        @option:created="handleAddNewFolder"
        @search="handleFolderSearch"
        append-to-body
      >
        <template v-slot:label>Folder</template>
      </NrSelect>

      <NrSelect
        v-model="docVisibility"
        v-if="enableDocVisibilityAssignment"
        :clearable="clearableVisibilityOption"
        :options="filteredVisibilityOptions"
        placeholder="select an option"
        @input="setDocVisibilityOption"
        @search="setDocVisibilityOption"
        append-to-body
      >
        <template v-slot:label>Document visibility</template>
      </NrSelect>

      <NrSelect
        v-if="requesterOrPublic"
        v-model="selectedMessageTemplates"
        :options="messageTemplates"
        placeholder="Search and select message template(s)"
        @search="filterMessageTemplates"
        @input="setMessageTemplate"
        @open="filterMessageTemplates"
        @load-more="loadMoreMessageTemplates"
        :loading="messageTemplateState.fetching"
        :hasMore="!messageTemplateState.end"
        multiple
        append-to-body
      >
        <template v-slot:label>Message template</template>
      </NrSelect>

      <NrEditor
        v-if="requesterOrPublic"
        v-model="message"
        :bulletList="true"
        :orderedList="true"
        :link="true"
        :indent="true"
        :outdent="true"
        :fontSize="true"
      />
      <NrFormInput
        v-if="requesterOrPublic"
        label="Yes, send to requester (required)"
        type="checkbox"
        @change="onRequesterChecked"
      />
    </form>
  </NrModal>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { url } from '@vuelidate/validators'
import DragNDrop from './DragNDrop.vue'
import NrEditor from '@nextrequestco/components/src/components/NrEditor'
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import NrTabs, { NrTab } from '@nextrequestco/components/src/components/NrTabs'
import NrDatePicker from '@nextrequestco/components/src/components/NrDatePicker'
import NrIcon from '@nextrequestco/components/src/components/NrIcon'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput'
import initEvaporate from '../../mixins/init_evaporate'

export default {
  mixins: [initEvaporate],
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return {
      docLink: { url }
    }
  },
  components: {
    NrModal,
    DragNDrop,
    NrEditor,
    NrSelect,
    NrTabs,
    NrTab,
    NrDatePicker,
    NrIcon,
    NrFormInput
  },
  props: {
    enableDocVisibilityAssignment: {
      type: Boolean,
      default: false
    },
    enableLinksTab: {
      type: Boolean,
      default: false
    },
    enableFolderAssignment: {
      type: Boolean,
      default: false
    },
    folder: {
      required: false,
      default: null
    },
    folders: {
      required: true,
      type: Array
    },
    uploadedDocs: {
      required: true,
      type: Array
    },
    uploadLimit: {
      default: 100,
      type: Number
    },
    clearableVisibilityOption: {
      type: Boolean,
      default: true
    },
    fullScreen: {
      type: Boolean,
      default: false
    },
    account: {
      type: Object
    },
    requestId: {
      type: String
    },
    messageTemplates: {
      type: Array,
      default: () => []
    },
    messageTemplateState: {
      type: Object,
      default: () => {}
    },
    show: { type: Boolean, default: false }
  },

  data() {
    return {
      selectedMessageTemplates: [],
      activeTabId: 'documents-upload-tab',
      docLink: '',
      docLinkDate: '',
      docLinkDescription: '',
      allFilesUploadedToS3: false,
      docVisibility: 'Staff Only' || '',
      selectedDocVisibilityOption: 'staff',
      searchTerm: '',
      tabLabels: ['Upload', 'Links'],
      visibilityOptions: ['Public', 'Requester + staff', 'Staff Only'],
      sendMessageToRequester: false,
      message: ''
    }
  },

  computed: {
    enablePageScroll() {
      return this.requesterOrPublic ? 'modal-scroll' : ''
    },

    filteredVisibilityOptions() {
      if (this.requestId) {
        return this.visibilityOptions
      }
      return this.visibilityOptions.filter((opt) => opt !== 'Requester + staff')
    },

    requesterOrPublic() {
      return (
        !!(
          this.docVisibility === 'Requester + staff' ||
          this.docVisibility === 'Public'
        ) && this.requestId
      )
    },

    selectedFolder() {
      return this.folder && typeof this.folder !== 'string' ? [this.folder] : []
    },

    uploadTabActive() {
      return this.activeTabId === 'documents-upload-tab'
    },

    invalidUrl() {
      return this.v$.docLink.url.$invalid
    },

    withMessage() {
      return !!this.message.length
    },

    releasingDocuments() {
      return !!this.uploadedDocs.length && this.requesterOrPublic
    },

    releasingDocumentsWithNoMessage() {
      return this.releasingDocuments && !this.withMessage
    },

    releasingDocumentsWithMessage() {
      return (
        this.releasingDocuments &&
        this.withMessage &&
        this.sendMessageToRequester
      )
    },

    staffOnlyDocs() {
      return !!this.uploadedDocs.length && !this.requesterOrPublic
    },

    withLink() {
      return !!this.docLink.length && !this.invalidUrl
    },

    releasingLink() {
      return this.withLink && this.requesterOrPublic
    },

    releasingLinkWithNoMessage() {
      return this.releasingLink && !this.withMessage
    },

    releasingLinkWithMessage() {
      return (
        this.releasingLink && this.withMessage && this.sendMessageToRequester
      )
    },

    staffOnlyLink() {
      return this.withLink && !this.requesterOrPublic
    },

    readyToSave() {
      if (this.uploadTabActive) {
        return (
          (this.releasingDocumentsWithMessage ||
            this.releasingDocumentsWithNoMessage ||
            this.staffOnlyDocs) &&
          this.allFilesUploadedToS3
        )
      } else {
        return (
          this.releasingLinkWithMessage ||
          this.releasingLinkWithNoMessage ||
          this.staffOnlyLink
        )
      }
    }
  },

  methods: {
    onClose() {
      this.$emit('on-close-modal')
      if (this.evaporate.evaporatingCount > 0) {
        this.evaporate.cancel().catch((e) => {
          console.error(e)
          window?.Sentry?.captureException(
            new Error('Error in Evaporate.cancel on modal close', { cause: e })
          )
        })
      }
      this.resetModal()
    },

    onRequesterChecked(ev) {
      const checked = ev.target.checked
      this.sendMessageToRequester = checked
    },

    filterMessageTemplates(searchText = '') {
      this.$emit('on-message-template-search', searchText)
    },

    loadMoreMessageTemplates() {
      this.$emit('on-message-template-scroll')
    },

    setMessageTemplate() {
      this.message = this.selectedMessageTemplates
        .map((temp) => temp.description)
        .join('')
    },

    /**
     * @fires {string || object} create-folder
     * @event folder
     *
     * @param {string || object} folder
     * Can be a string if array is empty to begin with
     */
    handleAddNewFolder(folder) {
      this.$emit('create-folder', folder)
    },

    /**
     * @fires {string} selected-folder
     * @event folderName
     *
     * @params {string} folderName
     */

    handleSelectedFolder(folderName) {
      this.searchTerm = ''
      this.$emit('selected-folder', folderName)
    },

    handleFolderSearch(query) {
      this.searchTerm = query
      this.$emit('search-folder', query)
    },

    setDocVisibilityOption(visibilityOption) {
      switch (visibilityOption) {
        case 'Public':
          this.selectedDocVisibilityOption = 'public'
          break
        case 'Requester + staff':
          this.selectedDocVisibilityOption = 'requester'
          break
        case 'Staff Only':
          this.selectedDocVisibilityOption = 'staff'
          break
      }
    },

    setDocumentLink(link) {
      this.docLink = link
    },

    setDocLinkDescription(docLinkDescription) {
      this.docLinkDescription = docLinkDescription
    },

    setupPayload(documents) {
      const state = this.selectedDocVisibilityOption
      const payload = {
        documents,
        state
      }

      if (this.folder && this.folder.label) {
        Object.assign(payload, { folder: this.folder.label })
      }

      if (this.message && this.requestId) {
        Object.assign(payload, {
          message: this.message,
          message_template_ids: this.selectedMessageTemplates.map(
            (t) => t.value
          )
        })
      }

      if (this.requestId) {
        Object.assign(payload, { request_id: this.requestId })
      }

      return payload
    },

    resetModal() {
      this.docLink = ''
      this.docLinkDate = ''
      this.docLinkDescription = ''
      this.selectedMessageTemplates = []
      this.selectedDocVisibilityOption = 'staff'
      this.message = ''
      this.sendMessageToRequester = false
      this.searchTerm = ''
      this.docVisibility = 'Staff Only' || ''
    },

    submitUploadForm() {
      let payload
      if (this.uploadTabActive) {
        payload = this.setupPayload(this.uploadedDocs)
      } else {
        const link = [
          {
            description: this.docLinkDescription,
            url: this.docLink,
            title: this.docLink,
            filename: this.docLink,
            doc_date: this.docLinkDate || '',
            link: true
          }
        ]
        payload = this.setupPayload(link)
      }
      this.$emit('on-submit', payload)
      this.$emit('on-close-modal')
      this.resetModal()
    }
  }
}
</script>

<style lang="scss">
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';

.document-upload-modal {
  .nr-tabs {
    margin-right: -$spacing-16;
    margin-left: -$spacing-16;
  }
  .nr-datepicker {
    margin-top: $spacing-12;
    .nr-datepicker-calendar {
      z-index: 2;
    }
  }
}
</style>
